import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import {
  verificationApi,
  type CarrierMappingInput,
  type ColumnMapping,
} from '../api/verificationApi';
import { ivKeys } from '../queryKeys';
import { useIvPracticeId } from './useIvPracticeId';

export function useImportFields() {
  const practiceId = useIvPracticeId();
  const query = useQuery({
    queryKey: ivKeys.importFields(practiceId),
    queryFn: () => verificationApi.getImportFields(),
    enabled: !!practiceId,
  });
  return { ...query, fields: query.data?.fields ?? [] };
}

export function useImportBatches() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';

  const query = useQuery({
    queryKey: ivKeys.batches(locationId),
    queryFn: () => verificationApi.listBatches(locationId),
    enabled: !!locationId,
  });

  return { ...query, batches: query.data ?? [], locationId };
}

export function useDeleteImportBatch() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (batchId: string) => verificationApi.deleteBatch(batchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ivKeys.batches(locationId) });
      queryClient.invalidateQueries({ queryKey: ivKeys.worklistPrefix(locationId) });
      queryClient.invalidateQueries({ queryKey: ivKeys.metrics(locationId) });
    },
  });
}

export function usePayerSearch(q: string) {
  const trimmed = q.trim();
  const query = useQuery({
    queryKey: ivKeys.payerSearch(trimmed),
    queryFn: () => verificationApi.searchPayers(trimmed),
    enabled: trimmed.length >= 2,
    staleTime: 60_000,
  });
  return { ...query, results: query.data ?? [] };
}

/**
 * All the step mutations for the import stepper. The parsed rows stay on the
 * server; each step passes the `importId` from the parse result, so none of
 * these mutations cache anything themselves.
 */
export function useImportFlow() {
  const { currentLocation } = useLocation();
  const locationId = currentLocation?.id ?? '';
  const practiceId = useIvPracticeId();
  const queryClient = useQueryClient();

  const parseFile = useMutation({
    mutationFn: (file: File) => verificationApi.parseFile(locationId, file),
  });

  const parseSample = useMutation({
    mutationFn: () => verificationApi.parseSample(locationId),
  });

  const saveMapping = useMutation({
    mutationFn: (mapping: ColumnMapping) => verificationApi.saveMapping(locationId, mapping),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ivKeys.importFields(practiceId) });
    },
  });

  const resolveCarriers = useMutation({
    mutationFn: ({ importId, mapping }: { importId: string; mapping: ColumnMapping }) =>
      verificationApi.resolveCarriers(locationId, importId, mapping),
  });

  const confirmCarriers = useMutation({
    mutationFn: (mappings: CarrierMappingInput[]) =>
      verificationApi.confirmCarriers(locationId, mappings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ivKeys.connectionsPrefix() });
    },
  });

  const preview = useMutation({
    mutationFn: ({ importId, mapping }: { importId: string; mapping: ColumnMapping }) =>
      verificationApi.previewImport(locationId, importId, mapping),
  });

  const commit = useMutation({
    mutationFn: ({ importId, mapping }: { importId: string; mapping: ColumnMapping }) =>
      verificationApi.commitImport(locationId, importId, mapping),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ivKeys.worklistPrefix(locationId) });
      queryClient.invalidateQueries({ queryKey: ivKeys.metrics(locationId) });
      queryClient.invalidateQueries({ queryKey: ivKeys.batches(locationId) });
    },
  });

  const downloadTemplate = useMutation({
    mutationFn: () => verificationApi.downloadTemplateCsv(),
  });

  return {
    locationId,
    parseFile,
    parseSample,
    saveMapping,
    resolveCarriers,
    confirmCarriers,
    preview,
    commit,
    downloadTemplate,
  };
}
